import { Box, Button, Text } from "@shopify/polaris";

const SB_SALES_BASE = "https://admin.schuilenburg.be/sales";

function allLinesFulfilled(lines) {
  if (!Array.isArray(lines)) return false;
  let ok = true;
  lines.forEach((line) => {
    if (line.aantalFulfilled < line.aantal && line.produktnr > 50) ok = false;
  });
  return ok;
}

function machineQty(lines) {
  if (!Array.isArray(lines)) return 0;
  return lines.reduce((acc, line) => {
    if (line.produktnr === 10956 || line.produktnr === 10957) acc += line.aantal;
    return acc;
  }, 0);
}

function serialsConnected(lines, serials) {
  const need = machineQty(lines);
  const have = Array.isArray(serials) ? serials.length : 0;
  return need <= have;
}

function internalDocsReady(sale) {
  const inv = sale?.invoices?.length ?? 0;
  const del = sale?.deliveries?.length ?? 0;
  return inv > 0 && del > 0;
}

function tileColors(complete) {
  if (complete) {
    return {
      background: "#eef9f2",
      borderColor: "#bfe3ca",
      titleColor: "#1f5134",
      textColor: "#47715b",
      badgeBackground: "#d8efe1",
      badgeColor: "#2f6a49",
      indicatorBackground: "#d8efe1",
      indicatorColor: "#2f6a49",
      linkColor: "#2f6a49",
      shadow: "0 2px 8px rgba(22, 130, 79, 0.05)",
    };
  }

  return {
    background: "#f6f7f8",
    borderColor: "#e3e6ea",
    titleColor: "#2f3847",
    textColor: "#687284",
    badgeBackground: "#eceff3",
    badgeColor: "#5e6878",
    indicatorBackground: "#e6e9ed",
    indicatorColor: "#7a8493",
    linkColor: "#4f5d73",
    shadow: "0 1px 4px rgba(15, 23, 42, 0.04)",
  };
}

function StepTile({ step, sbUrl, stepThreeAction, stepFiveAction }) {
  const colors = tileColors(step.complete);
  const indicatorLabel = step.complete ? "Done" : step.disabled ? "Later" : "Pending";

  return (
    <div
      style={{
        minHeight: "92px",
        height: "100%",
        borderRadius: "12px",
        border: `1px solid ${colors.borderColor}`,
        background: colors.background,
        boxShadow: colors.shadow,
        padding: "10px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        gap: "8px",
      }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px" }}>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            borderRadius: "999px",
            padding: "3px 9px",
            fontSize: "10px",
            fontWeight: 650,
            letterSpacing: "0.03em",
            textTransform: "uppercase",
            background: colors.badgeBackground,
            color: colors.badgeColor,
          }}>
          {step.title}
        </span>
        <span
          aria-label={indicatorLabel}
          title={indicatorLabel}
          style={{
            width: "22px",
            height: "22px",
            borderRadius: "999px",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "12px",
            fontWeight: 700,
            background: colors.indicatorBackground,
            color: colors.indicatorColor,
            flexShrink: 0,
          }}>
          {step.complete ? "✓" : step.disabled ? "•" : "○"}
        </span>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
        <Text as="p" variant="bodyMd" fontWeight="semibold">
          <span style={{ color: colors.titleColor, lineHeight: 1.25, fontSize: "0.84rem" }}>{step.subtitle}</span>
        </Text>
        <Text as="p" variant="bodySm">
          <span style={{ color: colors.textColor, lineHeight: 1.3, fontSize: "0.74rem" }}>{step.helperText}</span>
        </Text>
      </div>

      {step.key === 3 && stepThreeAction ? (
        <Button size="micro" variant="primary" onClick={stepThreeAction.onAction}>
          {stepThreeAction.label}
        </Button>
      ) : step.key === 5 && stepFiveAction ? (
        <Button size="micro" variant="primary" disabled={stepFiveAction.disabled} loading={stepFiveAction.loading} onClick={stepFiveAction.onAction}>
          {stepFiveAction.label}
        </Button>
      ) : step.key === 4 && sbUrl ? (
        <a
          href={sbUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            width: "fit-content",
            fontSize: "12px",
            fontWeight: 600,
            color: colors.linkColor,
            textDecoration: "underline",
            textUnderlineOffset: "2px",
          }}>
          Open SB order
        </a>
      ) : (
        <span />
      )}
    </div>
  );
}

export default function FulfilmentStepsProgress({ sale, stepThreeAction, stepFiveAction }) {
  const lines = sale?.lines;
  const serials = sale?.serials;
  const orderId = sale?.order?.order_id;
  const sbUrl = orderId != null && orderId !== "" ? `${SB_SALES_BASE}/${orderId}` : null;

  const s1 = allLinesFulfilled(lines);
  const s2 = serialsConnected(lines, serials);
  const packages = Array.isArray(sale?.packages) ? sale.packages : [];
  const s3 = packages.length > 0 && packages.every((orderPackage) =>
    [orderPackage.length_cm, orderPackage.width_cm, orderPackage.height_cm, orderPackage.weight_kg].every(
      (value) => Number.isFinite(Number(value)) && Number(value) > 0
    )
  );
  const s4 = internalDocsReady(sale);
  const s5 = Boolean(sale?.order?.sendcloud_id);

  const steps = [
    {
      key: 1,
      title: "Step 1",
      subtitle: "Fulfill all lines",
      helperText: "Every required sales line has been fulfilled.",
      complete: s1,
      disabled: false,
    },
    {
      key: 2,
      title: "Step 2",
      subtitle: "Connect all serial numbers",
      helperText: "All required machine serial numbers are linked to this order.",
      complete: s2,
      disabled: false,
    },
    {
      key: 3,
      title: "Step 3",
      subtitle: "Packaging material / boxes",
      helperText: s3 ? `${packages.length} package${packages.length === 1 ? "" : "s"} ready for Sendcloud.` : "Add packages, dimensions and weight.",
      complete: s3,
      disabled: false,
    },
    {
      key: 4,
      title: "Step 4",
      subtitle: "Create internal docs",
      helperText: "Done once both a delivery note and invoice are linked.",
      complete: s4,
      disabled: false,
    },
    {
      key: 5,
      title: "Step 5",
      subtitle: "Send to carrier",
      helperText: s5
        ? "A Sendcloud label has been created from the sales order page."
        : s1
          ? "Create the Sendcloud label in-app and print it immediately."
          : "Get a shipping quote now. The label can be created after fulfillment.",
      complete: s5,
      disabled: false,
    },
  ];

  return (
    <Box paddingBlock="200">
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
          gap: "10px",
          width: "100%",
        }}>
        {steps.map((step) => (
          <StepTile key={step.key} step={step} sbUrl={sbUrl} stepThreeAction={step.key === 3 ? stepThreeAction : null} stepFiveAction={step.key === 5 ? stepFiveAction : null} />
        ))}
      </div>
    </Box>
  );
}
